import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import { ArrowLeft, ArrowRight, Plus, X, Check, Upload } from 'lucide-react';
import InvitationCard from '../components/InvitationCard';
import PhoneFrame from '../components/PhoneFrame';
import LogoCropModal from '../components/LogoCropModal';

const STEPS = ['Overview', 'Sessions & RSVP', 'Review'];

const CATEGORY_COLORS = {
  Corporate: { colorFrom: '#2563eb', colorTo: '#1e3a8a' },
  Wedding: { colorFrom: '#a855f7', colorTo: '#6d28d9' },
  Academic: { colorFrom: '#0891b2', colorTo: '#155e75' },
  Entertainment: { colorFrom: '#e11d48', colorTo: '#881337' },
  Sports: { colorFrom: '#65a30d', colorTo: '#3f6212' },
  Personal: { colorFrom: '#ec4899', colorTo: '#be185d' },
  Social: { colorFrom: '#f97316', colorTo: '#ea580c' },
};

const CreateEventPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    time: '',
    venue: '',
    description: '',
    cardMessage: 'Request the honour of your presence.',
    totalGuests: '',
    category: 'Corporate',
    allowDeclinedRsvp: true,
    allowNonResponders: true,
  });
  const [sessions, setSessions] = useState([{ name: '', time: '', maxEntry: 1 }]);

  const [logoUrl, setLogoUrl] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const fileInputRef = useRef(null);

  const update = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropConfirm = (dataUrl) => {
    setLogoUrl(dataUrl);
    setCropOpen(false);
    setCropSrc(null);
  };

  const addSession = () => setSessions([...sessions, { name: '', time: '', maxEntry: 1 }]);
  const removeSession = (index) => setSessions(sessions.filter((_, i) => i !== index));
  const updateSession = (index, field, value) => {
    const next = [...sessions];
    next[index][field] = value;
    setSessions(next);
  };

  const stepValid = () => {
    if (step === 1) {
      return formData.name && formData.date && formData.time && formData.venue && formData.totalGuests;
    }
    return true;
  };

  const goNext = () => {
    if (!stepValid()) {
      toast({ title: 'Fill in the required fields', variant: 'destructive' });
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/events', {
        name: formData.name,
        date: formData.date,
        time: formData.time,
        venue: formData.venue,
        description: formData.description,
        totalGuests: Number(formData.totalGuests),
        sessions: sessions
          .filter((s) => s.name && s.time)
          .map((s) => ({ name: s.name, time: s.time, maxEntry: Number(s.maxEntry) || 1 })),
      });
      toast({
        title: 'Event created successfully!',
        description: 'Your event has been created and is ready for guests.',
      });
      navigate('/dashboard/events');
    } catch (error) {
      toast({
        title: 'Failed to create event',
        description: error?.error?.message || 'Please check the form and try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/events')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Create New Event</h1>
          <p className="text-gray-600 mt-1">Set up your event details and invitations</p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const active = n === step;
          const done = n < step;
          return (
            <React.Fragment key={label}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    done
                      ? 'bg-green-500 text-white'
                      : active
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {done ? <Check className="w-4 h-4" /> : n}
                </div>
                <span className={`text-sm ${active ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                  {label}
                </span>
              </div>
              {n < STEPS.length && <div className="flex-1 h-px bg-gray-200 mx-4" />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 space-y-6">
          {step === 1 && (
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Event Overview</h2>
              <div>
                <Label htmlFor="name">Event Name *</Label>
                <Input
                  id="name"
                  placeholder="Annual Tech Conference 2026"
                  value={formData.name}
                  onChange={(e) => update('name', e.target.value)}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Date *</Label>
                  <Input id="date" type="date" value={formData.date} onChange={(e) => update('date', e.target.value)} required />
                </div>
                <div>
                  <Label htmlFor="time">Time *</Label>
                  <Input id="time" type="time" value={formData.time} onChange={(e) => update('time', e.target.value)} required />
                </div>
              </div>
              <div>
                <Label htmlFor="venue">Venue *</Label>
                <Input
                  id="venue"
                  placeholder="Grand Convention Center"
                  value={formData.venue}
                  onChange={(e) => update('venue', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="totalGuests">Total Guests Expected *</Label>
                <Input
                  id="totalGuests"
                  type="number"
                  placeholder="500"
                  value={formData.totalGuests}
                  onChange={(e) => update('totalGuests', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="cardMessage">Message on Card</Label>
                <Input
                  id="cardMessage"
                  maxLength={60}
                  value={formData.cardMessage}
                  onChange={(e) => update('cardMessage', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your event..."
                  rows={4}
                  value={formData.description}
                  onChange={(e) => update('description', e.target.value)}
                />
              </div>

              <div>
                <Label>Event Category</Label>
                <Select value={formData.category} onValueChange={(v) => update('category', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(CATEGORY_COLORS).map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Event Logo / Image</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoFile}
                />
                <div
                  className="mt-1 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 py-8 px-4 text-center cursor-pointer hover:border-blue-400 transition relative"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {logoUrl ? (
                    <>
                      <img
                        src={logoUrl}
                        alt="Event logo preview"
                        className="w-20 h-20 rounded-full object-cover border"
                      />
                      <button
                        type="button"
                        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white border shadow flex items-center justify-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLogoUrl(null);
                        }}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-gray-400" />
                      <p className="text-sm text-gray-500">
                        Drag and drop a photo here or click to open file
                      </p>
                    </>
                  )}
                </div>
              </div>
            </Card>
          )}

          {step === 2 && (
            <>
              <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-xl font-semibold">Event Sessions</h2>
                    <p className="text-sm text-gray-600">Define different sessions or zones for your event</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addSession}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Session
                  </Button>
                </div>
                <div className="space-y-4">
                  {sessions.map((session, index) => (
                    <div key={index} className="flex gap-4 p-4 border rounded-lg">
                      <div className="flex-1 space-y-3">
                        <Input
                          placeholder="Session name (e.g., Opening Ceremony)"
                          value={session.name}
                          onChange={(e) => updateSession(index, 'name', e.target.value)}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            placeholder="Time (e.g., 09:00-10:00)"
                            value={session.time}
                            onChange={(e) => updateSession(index, 'time', e.target.value)}
                          />
                          <Input
                            type="number"
                            placeholder="Max entry"
                            value={session.maxEntry}
                            onChange={(e) => updateSession(index, 'maxEntry', parseInt(e.target.value, 10))}
                            min="1"
                          />
                        </div>
                      </div>
                      {sessions.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeSession(index)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6 space-y-3">
                <h2 className="text-xl font-semibold mb-2">RSVP Settings</h2>
                <label className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.allowDeclinedRsvp}
                    onChange={(e) => update('allowDeclinedRsvp', e.target.checked)}
                    className="w-4 h-4"
                  />
                  Allow guests who declined the RSVP to still check in
                </label>
                <label className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.allowNonResponders}
                    onChange={(e) => update('allowNonResponders', e.target.checked)}
                    className="w-4 h-4"
                  />
                  Allow guests who never responded to still check in
                </label>
              </Card>
            </>
          )}

          {step === 3 && (
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Review</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Event Name</p>
                  <p className="font-medium">{formData.name || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Date &amp; Time</p>
                  <p className="font-medium">
                    {formData.date || '—'} {formData.time}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Venue</p>
                  <p className="font-medium">{formData.venue || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Expected Guests</p>
                  <p className="font-medium">{formData.totalGuests || '—'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-gray-500">Sessions</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {sessions.filter((s) => s.name).length === 0 && <p className="font-medium">None</p>}
                    {sessions
                      .filter((s) => s.name)
                      .map((s, i) => (
                        <Badge key={i} variant="outline">
                          {s.name}
                        </Badge>
                      ))}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-between gap-3">
            <Button type="button" variant="outline" onClick={step === 1 ? () => navigate('/dashboard/events') : goBack}>
              {step === 1 ? 'Cancel' : (
                <>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </>
              )}
            </Button>
            {step < STEPS.length ? (
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={goNext}>
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button className="bg-orange-500 hover:bg-orange-600" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Creating...' : 'Create Event'}
              </Button>
            )}
          </div>
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-6 self-start">
          <p className="text-sm font-medium text-gray-600 mb-3">Invitation Preview</p>
          <PhoneFrame>
            <InvitationCard
              template={{
                name: formData.name || 'Your Event Name',
                category: formData.category,
                isPremium: false,
                ...CATEGORY_COLORS[formData.category],
              }}
              data={{
                eventTitle: formData.name || 'Your Event Name',
                tagline: formData.cardMessage,
                date: formData.date || 'Date',
                time: formData.time || '',
                venue: formData.venue || 'Venue',
                guestName: '[GUEST_NAME]',
                guestRole: '[GUEST_DETAILS]',
                logoUrl,
              }}
            />
          </PhoneFrame>
        </div>
      </div>

      <LogoCropModal
        open={cropOpen}
        imageSrc={cropSrc}
        onCancel={() => {
          setCropOpen(false);
          setCropSrc(null);
        }}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
};

export default CreateEventPage;
